/**
 * Wishlist and share controls for the product page.
 *
 * The wishlist is local to the browser: a list of product handles in
 * localStorage, no account and no server. That makes it survive reloads and
 * navigation on this device only, which is exactly what it promises.
 */

const STORAGE_KEY = 'nori:wishlist';

/**
 * Every read and write is guarded: Safari private mode, embedded webviews and
 * "block site data" settings all make localStorage throw rather than return
 * empty, and a saved-items button is never worth breaking a product page over.
 */
function readList() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeList(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

class NoriWishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.productHandle;
    if (!this.button || !this.handle) return;

    this.#render();
    this.button.addEventListener('click', this.#toggle);

    // Another tab, or a second button for the same product on one page.
    window.addEventListener('storage', this.#render);
    document.addEventListener('nori:wishlist:change', this.#render);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
    window.removeEventListener('storage', this.#render);
    document.removeEventListener('nori:wishlist:change', this.#render);
  }

  #render = () => {
    const saved = readList().includes(this.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    this.button.setAttribute(
      'aria-label',
      saved ? this.dataset.labelRemove || 'Remove from saved' : this.dataset.labelAdd || 'Save for later'
    );
  };

  #toggle = () => {
    const list = readList();
    const index = list.indexOf(this.handle);
    if (index === -1) {
      list.push(this.handle);
    } else {
      list.splice(index, 1);
    }

    if (!writeList(list)) return;

    this.#render();
    // Replays the pop each time rather than only on the first save.
    this.button.classList.remove('nori-product-actions__button--pop');
    void this.button.offsetWidth;
    this.button.classList.add('nori-product-actions__button--pop');

    document.dispatchEvent(
      new CustomEvent('nori:wishlist:change', { detail: { handle: this.handle, saved: index === -1 } })
    );
  };
}

class NoriShareButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.#share);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#share);
  }

  #share = async () => {
    const url = this.dataset.shareUrl || window.location.href;
    const title = this.dataset.shareTitle || document.title;

    // The native sheet where there is one — phones, and Safari on desktop.
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // An abort is the visitor closing the sheet, not a failure to handle.
        if (error?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#confirm();
    } catch {
      /* Nothing useful left to offer, so leave the page as it was. */
    }
  };

  #confirm() {
    this.setAttribute('data-copied', '');
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => this.removeAttribute('data-copied'), 2000);
  }
}

if (!customElements.get('nori-wishlist-button')) {
  customElements.define('nori-wishlist-button', NoriWishlistButton);
}

if (!customElements.get('nori-share-button')) {
  customElements.define('nori-share-button', NoriShareButton);
}
